import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resending, setResending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setUnverified(false);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate(next);
    } catch (err) {
      if (err.response?.data?.detail === "EMAIL_NOT_VERIFIED") {
        setUnverified(true);
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email, origin_url: window.location.origin });
      toast.success("Verification email sent — check your inbox");
    } catch {
      toast.error("Could not resend. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-5 py-16" data-testid="login-page">
      <div className="w-full max-w-md ufr-card rounded-md p-8">
        <div className="flex justify-center mb-6"><Logo size={56} /></div>
        <h1 className="font-display text-3xl text-white text-center">Welcome back</h1>
        <p className="text-white/60 text-sm text-center mt-1">Login to enter raffles and track your tickets.</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          {unverified && (
            <div className="border border-[color:var(--ufr-green)]/40 bg-[rgba(34,255,102,0.06)] rounded-md p-4 text-sm text-white/80" data-testid="unverified-notice">
              <div className="font-bold text-[color:var(--ufr-green)] mb-1">Verify your email first</div>
              Your account isn't verified yet. Check your inbox (and spam) for the verification link, or resend it below.
              <div className="text-xs text-white/60 mt-2">
                If you have not received an email from us, please resend the verification email or contact us on{" "}
                <a href="mailto:support@ultimatefootballraffles.com" className="text-[color:var(--ufr-green)] font-bold">support@ultimatefootballraffles.com</a>{" "}
                and we will assist you.
              </div>
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                className="btn-ufr-ghost px-4 py-2 text-xs mt-3 block"
                data-testid="login-resend-verification-btn"
              >
                {resending ? "Resending…" : "Resend verification email"}
              </button>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold block mb-1.5">Email</label>
            <input
              type="email"
              required
              className="ufr-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold block mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                className="ufr-input pr-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-[color:var(--ufr-green)] transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-ufr-primary w-full py-3 text-sm" data-testid="login-submit-btn">
            {loading ? "Logging in…" : "Login"}
          </button>
        </form>

        <div className="text-center text-sm text-white/60 mt-6">
          New here? <Link to="/register" className="text-[color:var(--ufr-green)] font-bold" data-testid="login-register-link">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
